import { Inject, Injectable } from '@angular/core';
import { QUINNY_AI_AVATAR_URL } from '@core';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { SettingsService } from '@delon/theme';
import { environment } from '@env/environment';
import { User } from 'devextreme/ui/chat';
import { marked } from 'marked';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';

export interface PromptCardRequestDto {
  id?: number;
  message?: string;
  additionalInstructions?: string;
  chatId?: string;
  agentId?: string;
  attachments?: Array<{ fileUrl: string }>;
}

export interface PromptCardPreviewResponseDto {
  executionId?: number;
  status?: string;
  answer?: string;
  errorMessage?: string;
  chatId?: string;
  threadId?: string;
  agentId?: string;
  startedAt?: string;
  completedAt?: string;
  analysisSummary?: string;
}

export interface PromptCardPreviewAsyncResponseDto {
  executionId?: number;
  status?: string;
  errorMessage?: string;
}

export interface ChatMessageFeedbackRequest {
  chatId: string;
  chatMessageId: number;
  score: number;
  comment?: string;
}

export interface StreamEvent {
  eventType?: string;
  chatId?: string;
  message?: string;
  stepId?: string;
  parentStepId?: string;
  stepLabel?: string;
  stepState?: 'pending' | 'active' | 'completed' | 'error';
  stepOrder?: number;
  argumentsPreview?: string;
  answer?: string;
  errorMessage?: string;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  creditsUsed?: number;
  quotaExceeded?: boolean;
}

export interface StreamHandlers {
  onEvent: (eventName: string, data: StreamEvent) => void;
  signal?: AbortSignal;
}

@Injectable({
  providedIn: 'root'
})
export class AiAssistantService {
  readonly REGENERATION_TEXT = 'Regeneration...';
  private errorList: Array<{ id: string; message: string }> = [];

  readonly user: User = { id: 'user' };
  readonly assistant = {
    id: 'assistant',
    name: 'Quinny AI',
    avatarUrl: QUINNY_AI_AVATAR_URL,
    disabled: true
  };

  constructor(
    private settings: SettingsService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService
  ) {}

  get aiServerUrl(): string {
    let serverUrl = environment.api['ai'];

    if (!serverUrl && !environment.production) {
      console.error('aiServerUrl is not set, please check environment.dev.ts');
    }
    // remove ending slash
    if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
    return serverUrl;
  }

  get mainApiUrl(): string {
    let serverUrl = environment.api.baseUrl || '';
    // remove ending slash
    if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
    return serverUrl;
  }

  private async tryFetch(
    instance: any,
    fetchAction: () => Promise<any>,
    message: string
  ): Promise<any> {
    try {
      return await fetchAction();
    } catch (error: any) {
      this.handleError(instance, { message: error.message, code: message });
    }
  }

  private handleError(
    instance: any,
    error: { message: string; code: string }
  ): void {
    const id = `id${Math.random().toString(16).slice(2)}`;
    setTimeout(() => {
      this.errorList = this.errorList.filter(err => err.id !== id);
      instance.option('alerts', this.errorList || []);
    }, 10000);
    this.errorList.push({
      id: id,
      message: `${error.code} - ${error.message}`
    });
    instance.option('alerts', this.errorList || []);
  }

  normalizeAIResponse(text: string): string {
    text = text.replace(/【\d+:\d+†[^\】]+】/g, '');
    let html = marked.parse(text, { async: false }) as string;
    if (/<p>\.\s*<\/p>\s*$/.test(html)) {
      html = html.replace(/<p>\.\s*<\/p>\s*$/, '');
    }
    return html;
  }

  async getAIResponse(
    instance: any,
    text: string,
    id: string
  ): Promise<string> {
    const token = this.tokenService.get()?.token;
    return this.tryFetch(
      instance,
      async () => {
        const response = await fetch(`${this.aiServerUrl}/api/AI/Conversation`, {
          method: 'POST',
          body: JSON.stringify({
            chatId: id,
            message: text,
            type: 'chat',
            platform: 'QCA'
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          this.handleError(instance, {
            code: `${response.status}`,
            message: `Internal server error`
          });
          return;
        }

        const result = await response.json();
        return result?.data?.answer ?? result?.answer ?? '';
      },
      'Conversation'
    );
  }

  async conversationStream(
    payload: Record<string, unknown>,
    handlers: StreamHandlers
  ): Promise<void> {
    const token = this.tokenService.get()?.token;
    const response = await fetch(
      `${this.aiServerUrl}/api/AI/ConversationStream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
        signal: handlers.signal
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to stream AI response: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Streaming response body is not available.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = this.processSseBuffer(buffer, handlers.onEvent);
    }

    buffer += decoder.decode();
    this.processSseBuffer(buffer, handlers.onEvent, true);
  }

  private processSseBuffer(
    buffer: string,
    onEvent: (eventName: string, data: StreamEvent) => void,
    flushRemaining = false
  ): string {
    let normalized = buffer.replace(/\r\n/g, '\n');
    let separatorIndex = normalized.indexOf('\n\n');

    while (separatorIndex >= 0) {
      const rawEvent = normalized.slice(0, separatorIndex).trim();
      normalized = normalized.slice(separatorIndex + 2);

      if (rawEvent.length > 0) {
        this.dispatchSseEvent(rawEvent, onEvent);
      }

      separatorIndex = normalized.indexOf('\n\n');
    }

    if (flushRemaining && normalized.trim().length > 0) {
      this.dispatchSseEvent(normalized.trim(), onEvent);
      return '';
    }

    return normalized;
  }

  private dispatchSseEvent(
    rawEvent: string,
    onEvent: (eventName: string, data: StreamEvent) => void
  ): void {
    const lines = rawEvent.split('\n');
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() || 'message';
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (dataLines.length === 0) {
      return;
    }

    const rawData = dataLines.join('\n');
    let parsedData: StreamEvent = {};

    try {
      parsedData = JSON.parse(rawData) as StreamEvent;
    } catch {
      parsedData = { message: rawData };
    }

    onEvent(eventName, parsedData);
  }

  private buildPromptCardRequestPayload(
    request: PromptCardRequestDto,
    requireId = false
  ): PromptCardRequestDto {
    const payload: PromptCardRequestDto = {
      id: request.id || undefined,
      message: request.message || undefined,
      additionalInstructions: request.additionalInstructions || undefined,
      agentId: request.agentId || undefined,
      chatId: request.chatId || undefined,
      attachments: request.attachments || []
    };

    if (requireId && !payload.id) {
      throw new Error('Prompt id is required.');
    }

    return payload;
  }

  private async postPromptCardRequest<TResponse>(
    endpoint: 'preview' | 'execute' | 'regenerate',
    request: PromptCardRequestDto,
    signal?: AbortSignal,
    requireId = false
  ): Promise<TResponse> {
    const token = this.tokenService.get()?.token;
    const payload = this.buildPromptCardRequestPayload(request, requireId);

    const response = await fetch(
      `${this.aiServerUrl}/api/PromptCards/${endpoint}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
        signal
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to ${endpoint} prompt: ${response.statusText}`);
    }

    return await response.json();
  }

  async previewPromptCard(
    request: PromptCardRequestDto,
    signal?: AbortSignal
  ): Promise<PromptCardPreviewResponseDto> {
    return this.postPromptCardRequest<PromptCardPreviewResponseDto>(
      'preview',
      request,
      signal
    );
  }

  async executePromptCard(
    request: PromptCardRequestDto,
    signal?: AbortSignal
  ): Promise<PromptCardPreviewAsyncResponseDto> {
    return this.postPromptCardRequest<PromptCardPreviewAsyncResponseDto>(
      'execute',
      request,
      signal,
      true
    );
  }

  async regeneratePromptCard(
    request: PromptCardRequestDto,
    signal?: AbortSignal
  ): Promise<PromptCardPreviewAsyncResponseDto> {
    return this.postPromptCardRequest<PromptCardPreviewAsyncResponseDto>(
      'regenerate',
      request,
      signal,
      true
    );
  }

  async getPromptCardPreviewByPromptCardId(
    promptCardId: number
  ): Promise<PromptCardPreviewResponseDto> {
    const token = this.tokenService.get()?.token;
    const response = await fetch(
      `${this.aiServerUrl}/api/PromptCards/preview?promptCardId=${promptCardId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get prompt preview: ${response.statusText}`);
    }

    return await response.json();
  }

  async failPromptCardExecution(
    executionId: number,
    reason?: string
  ): Promise<any> {
    const token = this.tokenService.get()?.token;
    const response = await fetch(
      `${this.aiServerUrl}/api/PromptCards/Executions/${executionId}/Fail`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: reason || undefined })
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to mark execution as failed: ${response.statusText}`
      );
    }

    return await response.json();
  }

  isRunning(status: string | undefined): boolean {
    return status === 'Pending' || status === 'Running';
  }

  async bindPromptThreadToChat(
    chatId: string,
    threadId: string,
    title?: string,
    agentId?: string
  ): Promise<any> {
    const token = this.tokenService.get()?.token;
    const payload = {
      chatId,
      threadId,
      title: title || undefined,
      agentId: agentId || undefined
    };

    const response = await fetch(
      `${this.aiServerUrl}/api/PromptCards/BindPromptThreadToChat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to bind prompt thread to chat: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async saveChatMessageFeedback(
    request: ChatMessageFeedbackRequest
  ): Promise<any> {
    const token = this.tokenService.get()?.token;
    const payload = {
      chatId: request.chatId,
      chatMessageId: request.chatMessageId,
      score: request.score,
      comment: request.comment || undefined
    };

    const response = await fetch(
      `${this.aiServerUrl}/api/AI/SaveChatMessageFeedback`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to save chat message feedback: ${response.statusText}`
      );
    }

    return await response.json();
  }

  renderAssistantMessage(instance: any, message: string): void {
    instance.option({ typingUsers: [] });
    instance.renderMessage({
      timestamp: new Date(),
      text: message,
      author: this.assistant.name,
      id: this.assistant.id
    });
  }

  convertToHtml(value: string): string {
    const result = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype)
      .use(rehypeStringify)
      .processSync(value)
      .toString();

    return result;
  }

  // ==================== Share Conversation Methods ====================

  /**
   * Create or get existing share link for a conversation
   */
  async shareConversation(chatId: string): Promise<CreateShareResponse | null> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/ShareConversation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ chatId })
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.success && result.data) {
        return {
          shareId: result.data.shareId,
          shareUrl: this.buildShareUrl(result.data.shareId),
          sharedAt: result.data.sharedAt
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Get shared conversation data (anonymous access)
   */
  async getSharedConversation(
    shareId: string
  ): Promise<SharedConversationResponse | null> {
    const url = `${this.aiServerUrl}/api/AI/GetSharedConversation/${shareId}`;

    try {
      // Anonymous request - no auth token
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();

      // Handle both response formats:
      // 1. Wrapped format: { success: true, data: {...} }
      // 2. Direct format: { shareId, title, messages, ... }
      let data: SharedConversationResponse | null = null;

      if (result.success && result.data) {
        data = result.data;
      } else if (result.shareId || result.title || result.messages) {
        // Check if result itself is the conversation data (direct format)
        data = result as SharedConversationResponse;
      }

      if (data) {
        // Normalize messages to ensure content and role fields are set correctly
        if (data.messages && Array.isArray(data.messages)) {
          data.messages = data.messages.map((msg: any) => {
            // Use IsFromAI property as the primary way to determine role
            let isAssistant = false;

            if (msg.IsFromAI !== undefined) {
              isAssistant = msg.IsFromAI === true;
            } else if (msg.isFromAI !== undefined) {
              isAssistant = msg.isFromAI === true;
            } else if (msg.role) {
              // Fallback to role field
              const rawRole = (msg.role || '').toLowerCase();
              isAssistant = [
                'assistant',
                'ai',
                'bot',
                'quinny',
                'system'
              ].includes(rawRole);
            }

            return {
              role: (isAssistant ? 'assistant' : 'user') as
                | 'user'
                | 'assistant',
              content:
                msg.content ||
                msg.text ||
                msg.message ||
                msg.Content ||
                msg.Text ||
                msg.Message ||
                '',
              timestamp:
                msg.timestamp ||
                msg.createdAt ||
                msg.Timestamp ||
                msg.CreatedAt ||
                ''
            };
          });
        }
        return data;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Continue a shared conversation (creates a new session for the current user)
   */
  async continueSharedConversation(
    shareId: string
  ): Promise<ContinueShareResponse | null> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/ContinueSharedConversation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ shareId, platform: 'QCA' })
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.success === false) {
        return null;
      }
      if (result.success && result.data) {
        return result.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Build the share URL for a given shareId
   */
  buildShareUrl(shareId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/#/full/ai/shared/${shareId}`;
  }

  /**
   * Check if the current user has access to a tenant
   * Uses the existing CanAccess method in UserTenantLinksRepository
   * @param tenantId The tenant ID to check access for
   * @returns TenantAccessCheckResponse with hasAccess flag and optional error message
   */
  async checkTenantAccess(
    tenantId: string
  ): Promise<TenantAccessCheckResponse> {
    const token = this.tokenService.get()?.token;

    // If no token, user is not authenticated
    if (!token) {
      return {
        hasAccess: false,
        errorMessage: 'Please log in to continue this conversation.'
      };
    }

    try {
      // Call the main QNE API server which uses UserTenantLinksRepository.CanAccess
      const response = await fetch(
        `${this.mainApiUrl}/api/Companies/CheckTenantAccess?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          return {
            hasAccess: false,
            errorMessage: 'Please log in to continue this conversation.'
          };
        }
        if (response.status === 403) {
          return {
            hasAccess: false,
            errorMessage:
              'You do not have access to this company. Please contact the company administrator to request access.'
          };
        }
        return {
          hasAccess: false,
          errorMessage: 'Unable to verify access. Please try again later.'
        };
      }

      const result = await response.json();
      // Handle the API response format: { success: true, data: { hasAccess, companyName, errorMessage } }
      const data = result.data || result;
      return {
        hasAccess: data.hasAccess ?? false,
        companyName: data.companyName,
        errorMessage: data.errorMessage
      };
    } catch {
      return {
        hasAccess: false,
        errorMessage: 'Unable to verify access. Please try again later.'
      };
    }
  }

  // ==================== Chat Session Management Methods ====================

  /**
   * Rename a chat session title
   */
  async renameChatSession(
    chatId: string,
    title: string
  ): Promise<RenameChatSessionResponse | null> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/RenameChatSession`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ chatId, title })
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Toggle pin status of a chat session
   */
  async togglePinChatSession(
    chatId: string
  ): Promise<TogglePinChatSessionResponse | null> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/TogglePinChatSession`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ chatId })
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Delete a chat session
   */
  async deleteChatSession(chatId: string): Promise<boolean> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/DeleteChatHistory?chatId=${encodeURIComponent(chatId)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Trigger AI-powered title generation for a chat session.
   * The generated title will be pushed to the frontend via SignalR.
   */
  async generateChatTitle(
    chatId: string
  ): Promise<GenerateChatTitleResponse | null> {
    const token = this.tokenService.get()?.token;
    try {
      const response = await fetch(
        `${this.aiServerUrl}/api/AI/GenerateChatTitle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ chatId })
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  // ==================== Quota Check Methods ====================

  /**
   * Check whether the tenant has exceeded their AI credits quota.
   * Used for pre-send blocking (ChatGPT-style).
   */
  async checkQuota(agentId?: string | null): Promise<QuotaCheckResult> {
    const token = this.tokenService.get()?.token;
    let url = `${this.aiServerUrl}/api/AI/CheckQuota`;
    if (agentId) {
      url += `?agentId=${encodeURIComponent(agentId)}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`CheckQuota failed with status ${response.status}`);
    }

    const result = await response.json();
    const data = result.data || result;
    return {
      plan: data.plan ?? '',
      subscriptionStart: data.subscriptionStart ?? '',
      subscriptionEnd: data.subscriptionEnd ?? '',
      planCredits: data.planCredits ?? -1,
      addOnCredits: data.addOnCredits ?? -1,
      totalCredits: data.totalCredits ?? -1,
      remainingCredits: data.remainingCredits ?? -1,
      isExceeded: data.isExceeded ?? false,
      refreshDate: data.refreshDate ?? '',
      remainingInputTokenBudget: data.remainingInputTokenBudget ?? -1,
      remainingOutputTokenBudget: data.remainingOutputTokenBudget ?? -1
    };
  }

  /**
   * Check whether the current user has permission to manage the subscription.
   * Used to enable/disable the "Increase AI Capacity" action.
   */
  async checkSubscriptionPermission(): Promise<boolean> {
    const token = this.tokenService.get()?.token;
    const tenantId = this.settings.user?.['company']?.tenantId;
    if (!token || !tenantId) return false;

    try {
      const response = await fetch(
        `${this.mainApiUrl}/api/subscription/check?_silent=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ id: tenantId, operation: 'view' })
        }
      );

      if (!response.ok) return false;
      const result = await response.json();
      return !!result;
    } catch {
      return false;
    }
  }

  // ==================== Usage & Credits Methods ====================

  /**
   * Get token usage data for the current tenant
   */
  async getTokenUsage(
    pageIndex: number = 1,
    pageSize: number = 10,
    startDate?: string,
    endDate?: string
  ): Promise<TokenUsageResponse> {
    const token = this.tokenService.get()?.token;

    try {
      let url = `${this.aiServerUrl}/api/AI/GetTokenUsage?pageIndex=${pageIndex}&pageSize=${pageSize}`;
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return this.getDefaultUsageResponse();
      }

      const result = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      if (result.records || result.summary) {
        return result;
      }

      return this.getDefaultUsageResponse();
    } catch {
      return this.getDefaultUsageResponse();
    }
  }

  private getDefaultUsageResponse(): TokenUsageResponse {
    return {
      summary: {
        totalCredits: 0,
        planCredits: 0,
        addOnCredits: 0,
        usedCredits: 0,
        refreshDate: '',
        subscriptionStart: '',
        subscriptionEnd: ''
      },
      records: [],
      totalRecords: 0,
      pageIndex: 1,
      pageSize: 10
    };
  }
}

// ==================== Share-related Interfaces ====================

export interface CreateShareResponse {
  shareId: string;
  shareUrl: string;
  sharedAt: string;
}

export interface SharedConversationResponse {
  shareId?: string;
  title: string;
  sharedBy?: string; // Masked email from API
  ownerName?: string;
  ownerEmail?: string;
  sharedAt: string;
  isExpired?: boolean;
  tenantId?: string; // Tenant ID for access validation
  companyName?: string; // Company name for display
  messages: SharedMessage[];
}

export interface SharedMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ContinueShareResponse {
  chatId: string;
  title: string;
  messages: SharedMessage[];
}

export interface TenantAccessCheckResponse {
  hasAccess: boolean;
  companyName?: string;
  errorMessage?: string;
}

// ==================== Chat Session Management Interfaces ====================

export interface RenameChatSessionResponse {
  chatId: string;
  title: string;
}

export interface TogglePinChatSessionResponse {
  chatId: string;
  isPinned: boolean;
  pinnedAt?: string;
}

export interface GenerateChatTitleResponse {
  chatId: string;
  title: string;
  generationTriggered: boolean;
}

// ==================== Quota Check Interfaces ====================

/**
 * Combined quota check and subscription billing result.
 * Contains both the plan details and the runtime quota enforcement state.
 */
export interface QuotaCheckResult {
  plan: string;
  subscriptionStart: string;
  subscriptionEnd: string;
  planCredits: number;
  addOnCredits: number;
  totalCredits: number;
  remainingCredits: number;
  isExceeded: boolean;
  refreshDate: string;
  remainingInputTokenBudget: number;
  remainingOutputTokenBudget: number;
}

// ==================== Usage & Credits Interfaces ====================

/**
 * Represents a single usage record (chat or prompt card)
 */
export interface UsageRecord {
  type: string;
  title: string;
  userEmail: string;
  date: string;
  creditsUsed: number;
}

/**
 * Summary of usage credits
 */
export interface UsageSummary {
  totalCredits: number;
  planCredits: number;
  addOnCredits: number;
  usedCredits: number;
  refreshDate: string;
  subscriptionStart: string;
  subscriptionEnd: string;
}

/**
 * Response from GetTokenUsage API
 */
export interface TokenUsageResponse {
  summary: UsageSummary;
  records: UsageRecord[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
}
